import { useMemo } from 'react';
import "react-datepicker/dist/react-datepicker.css";
import MetricsOverview from './components/MetricsOverview';
import MapboxHeatmap from './components/MapboxHeatmap';
import TotalScansChart from './components/TotalScansChart';
import QRPerformanceChart from './components/QRPerformanceChart';
import UniqueUsersChart from './components/UniqueUsersChart';
import GeographicInsights from './components/GeographicInsights';
import FilterControls from './components/FilterControls';
import { useAnalyticsView } from '../../utils/hooks/useAnalyticsView';
import styles from './AnalyticsView.module.css';

export default function AnalyticsView({ orgId, orgObj, liquidQRs }) {
    // Use our new analytics hook - this handles all data fetching, caching, and processing
    const analytics = useAnalyticsView(orgId, liquidQRs);
    const sortedCategories = useMemo(
        () => [...(orgObj?.categories || [])].sort((a, b) => a.name.localeCompare(b.name)),
        [orgObj?.categories]
    );

    // Show loading state
    if (analytics.loading) {
        return (
            <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>Loading analytics data...</p>
            </div>
        );
    }

    // Show error state
    if (analytics.error) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1 className={styles.title}>Analytics Dashboard</h1>
                </div>
                <div className={styles.noData}>
                    <h3>Error loading analytics data</h3>
                    <p>{analytics.error.message}</p>
                    <button onClick={analytics.refetch} className={styles.retryBtn}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Show empty state if no required data
    if (!orgObj || !liquidQRs || liquidQRs.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1 className={styles.title}>Analytics Dashboard</h1>
                </div>
                <div className={styles.noData}>
                    <h3>No QR codes found</h3>
                    <p>Create some QR codes to start tracking analytics.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.container} ${(analytics.isFetchingNewData || analytics.isProcessingData) ? styles.updating : ''}`}>
            <div className={styles.header}>
                <h1 className={styles.title}>Analytics Dashboard</h1>
            </div>

            <FilterControls
                timeFrame={analytics.timeFrame}
                customDateRange={analytics.customDateRange}
                selectedCategories={analytics.selectedCategories}
                searchQuery={analytics.searchQuery}
                categories={sortedCategories}
                onTimeFrameChange={analytics.handleTimeFrameChange}
                onDateRangeChange={analytics.handleDateRangeChange}
                onCategoryChange={analytics.handleCategoryChange}
                onSearchChange={analytics.handleSearchChange}
                filteredQRsCount={analytics.filteredQRs.length}
                qrsWithScansCount={analytics.processedData?.qrPerformanceData?.length || 0}
                isFetchingNewData={analytics.isFetchingNewData}
                isProcessingData={analytics.isProcessingData}
            />

            {analytics.processedData && analytics.processedData.metrics.totalScans > 0 ? (
                <>
                    <MetricsOverview metrics={analytics.processedData.metrics} />

                    <MapboxHeatmap
                        data={analytics.processedData.ipHeatmapData.locations}
                        locationStats={analytics.processedData.ipHeatmapData.stats}
                    />

                    <div className={styles.chartsGrid}>
                        <div className={styles.chartCard}>
                            <TotalScansChart data={analytics.processedData.timeSeriesData} />
                        </div>

                        <div className={styles.chartCard}>
                            <UniqueUsersChart data={analytics.processedData.uniqueUsersData} />
                        </div>

                        <div className={styles.chartCard}>
                            <QRPerformanceChart data={analytics.processedData.qrPerformanceData} />
                        </div>

                        <div className={styles.chartCard}>
                            <GeographicInsights
                                data={analytics.processedData.ipHeatmapData.cityLocations}
                                locationStats={analytics.processedData.ipHeatmapData.stats}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <div className={styles.noData}>
                    <h3>No data available</h3>
                    <p>
                        {analytics.filteredQRs.length === 0 
                            ? "No QR codes match your current filters."
                            : "No scan data found for the selected time period. Try adjusting your filters or date range."
                        }
                    </p>
                    {process.env.NODE_ENV === 'development' && (
                        <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
                            <p>Debug info:</p>
                            <ul>
                                <li>Filtered QRs: {analytics.filteredQRs.length}</li>
                                <li>Cache size: {analytics.cacheStats.size} days</li>
                                <li>Time frame: {analytics.timeFrame}</li>
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
